import {
	WordCloud as ReactWordCloud,
	type Word,
} from '@isoterik/react-word-cloud'
import { useEffect, useRef, useState } from 'react'

const COLORS = [
	'#0069b4', // sapphire-blue
	'#e63719', // red
	'#00785f', // forest-green
	'#702283', // violet
	'#f9b000', // honey-yellow
	'#004696', // ocean-blue
	'#2c414a', // dark-grey
	'#365b6b', // medium-grey
]

function hashWord(word: string): number {
	let hash = 0
	for (let i = 0; i < word.length; i++) {
		hash = (hash << 5) - hash + word.charCodeAt(i)
		hash |= 0
	}
	return Math.abs(hash)
}

/** Rotação predominantemente horizontal, com poucas palavras verticais pra dar variedade. */
function rotationForWord(word: string): number {
	const options = [0, 0, 0, 0, 0, 0, -90, 90]
	return options[hashWord(word) % options.length]
}

function useContainerSize<T extends HTMLElement>() {
	const ref = useRef<T>(null)
	const [size, setSize] = useState({ width: 0, height: 0 })

	useEffect(() => {
		const el = ref.current
		if (!el) return

		const observer = new ResizeObserver(([entry]) => {
			const { width, height } = entry.contentRect
			setSize({ width: Math.floor(width), height: Math.floor(height) })
		})
		observer.observe(el)
		return () => observer.disconnect()
	}, [])

	return { ref, size }
}

export function WordCloud({
	words,
}: {
	words: { word: string; count: number }[]
}) {
	const { ref, size } = useContainerSize<HTMLDivElement>()

	if (words.length === 0) return null

	const data: Word[] = words.map((entry) => ({
		text: entry.word,
		value: entry.count,
	}))

	return (
		<div ref={ref} className="h-96 w-full min-w-0">
			{size.width > 0 && size.height > 0 && (
				<ReactWordCloud
					words={data}
					width={size.width}
					height={size.height}
					padding={2}
					spiral="archimedean"
					font="Aptos, sans-serif"
					fontWeight={(word) =>
						word.value >= (data[0]?.value ?? 0) * 0.6 ? 700 : 500
					}
					fontSize={(word) => {
						const max = data[0]?.value ?? 1
						const min = data[data.length - 1]?.value ?? 1
						const span = Math.max(max - min, 1)
						const ratio = (word.value - min) / span
						return 12 + ratio * 26
					}}
					rotate={(word) => rotationForWord(word.text)}
					fill={(word) => COLORS[hashWord(word.text) % COLORS.length]}
					transition="all 0.3s ease"
					enableTooltip={true}
					timeInterval={1}
					fontStyle={'normal'}
				/>
			)}
		</div>
	)
}
